import React, { useEffect, useState, useCallback } from "react";
import { Radar, Clock, AlertTriangle, CheckCircle2, ExternalLink, Loader2, CalendarClock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useDoxWork } from "@/context/DoxWorkContext";

const TIER_TONE = {
  120: "sky", 90: "sky", 60: "amber", 30: "amber", 14: "orange", 7: "orange", 3: "rose", 1: "rose",
};
const TONE_CLASSES = {
  sky:    "border-sky-400/30 text-sky-300",
  amber:  "border-amber-400/30 text-amber-300",
  orange: "border-dox-orange/40 text-dox-orange",
  rose:   "border-rose-400/40 text-rose-300",
  dead:   "border-rose-500/60 text-rose-300",
};

export default function CommandCenter() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/compliance/upcoming-renewals");
      setData(data);
    } catch { toast.error("Couldn't load timeline."); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const schedule = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/compliance/schedule-renewals", {});
      toast.success(`Dox scheduled ${data.created} new renewal(s).`);
      await load();
    } catch { toast.error("Scheduling failed."); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="h-64 rounded-2xl bg-white/5 animate-pulse" />;

  const overdue = data.items.filter((t) => t.overdue);
  const inFlight = data.items.filter((t) => !t.overdue && t.handoff_submission_id);
  const upcoming = data.items.filter((t) => !t.overdue && !t.handoff_submission_id);

  return (
    <div className="space-y-8" data-testid="command-center-view">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Radar size={16} className="text-dox-orange" />
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-dox-magenta">Compliance Command Center</div>
          </div>
          <h2 className="font-display font-black text-3xl">Say YES. I&apos;ll handle the rest.</h2>
          <p className="text-white/60 mt-2 max-w-2xl text-sm">
            One tap on any card and I&apos;ll prepare the package, file it, and monitor the agency until it&apos;s
            confirmed. I&apos;ll only interrupt you when a signature or payment is required.
          </p>
        </div>
        <button
          data-testid="cc-schedule-btn"
          onClick={schedule}
          disabled={busy}
          className="rounded-full bg-white/5 border border-white/15 hover:border-white/30 px-4 py-2 min-h-[44px] text-xs font-semibold text-white/80 transition-colors disabled:opacity-60 flex items-center gap-2"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <CalendarClock size={12} />}
          {busy ? "Refreshing…" : "Refresh schedule"}
        </button>
      </div>

      {overdue.length > 0 && (
        <Section title="Overdue — needs your attention" icon={AlertTriangle} tone="text-rose-300">
          <div className="grid md:grid-cols-2 gap-4">
            {overdue.map((t) => <RenewalCard key={t.id} task={t} onDone={load} />)}
          </div>
        </Section>
      )}

      {inFlight.length > 0 && (
        <Section title="I&apos;m on it" icon={Sparkles} tone="text-emerald-300">
          <div className="grid md:grid-cols-2 gap-4">
            {inFlight.map((t) => <RenewalCard key={t.id} task={t} onDone={load} />)}
          </div>
        </Section>
      )}

      {upcoming.length > 0 ? (
        <Section title="Coming up" icon={Clock} tone="text-white/85">
          <div className="grid md:grid-cols-2 gap-4">
            {upcoming.map((t) => <RenewalCard key={t.id} task={t} onDone={load} />)}
          </div>
        </Section>
      ) : overdue.length === 0 && inFlight.length === 0 ? (
        <div className="glass-card p-10 text-center">
          <CheckCircle2 size={28} className="text-emerald-400 mx-auto mb-3" />
          <div className="text-white/85 font-semibold">You&apos;re all clear.</div>
          <div className="text-white/45 text-sm mt-1">Complete your Roadmap profile and click &ldquo;Refresh schedule&rdquo; to have Dox lay out your next 12 months.</div>
        </div>
      ) : null}
    </div>
  );
}

function Section({ title, icon: Icon, tone, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className={tone} />
        <h3 className="font-display font-bold text-xl" dangerouslySetInnerHTML={{ __html: title }} />
      </div>
      {children}
    </div>
  );
}

function humanDays(days, overdue) {
  if (overdue) {
    const d = Math.abs(days);
    return d === 0 ? "today" : `${d} day${d === 1 ? "" : "s"} overdue`;
  }
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

/**
 * The core "Want me to renew this? YES" action card.
 * - Passive state:  ⚠️ Electrical License expires in 21 days. — [Renew it for me? YES]
 * - Working state:  loading spinner + Dox voice steps
 * - Done state:     "I've prepared and filed it. I'll monitor until confirmed."
 */
function RenewalCard({ task, onDone }) {
  const rec = task.recommendation || {};
  const tone = task.overdue ? "dead" : (TIER_TONE[task.reminder_tier] || "sky");
  const cls = TONE_CLASSES[tone];
  const [state, setState] = useState(task.handoff_submission_id ? "done" : "idle"); // idle | working | done | error
  const [handoff, setHandoff] = useState(task.handoff_submission_id ? { submission_id: task.handoff_submission_id } : null);

  const label = task.title?.replace(/^Renew:\s*/i, "") || "This item";
  const whenPhrase = task.overdue ? `is ${humanDays(task.days_remaining, true)}` : `expires ${humanDays(task.days_remaining, false)}`;
  const { runDoxWork } = useDoxWork();

  const doYes = () => {
    if (!runDoxWork) return doYesFallback();
    runDoxWork({
      ackLine: "I've got it.",
      deliverLine: "Filed.",
      stages: [
        { label: "Reviewing your renewal", emoji: "📄", code: "reviewing" },
        { label: "Verifying with the agency", emoji: "✅", code: "verifying" },
        { label: "Preparing the filing package", emoji: "✍️", code: "preparing" },
        { label: "Submitting on your behalf", emoji: "📤", code: "filing" },
      ],
      run: () => api.post("/compliance/handoff", { task_id: task.id }),
      onResolved: ({ data }) => {
        setHandoff(data);
        setState("done");
        const leadTitle = data?.lead_specialist?.title || "Licensing Specialist";
        toast.success(`On it. My ${leadTitle} has the packet.`);
        onDone && onDone();
      },
      onError: () => toast.error("Couldn't hand this off."),
    });
  };

  const doYesFallback = async () => {
    setState("working");
    try {
      const { data } = await api.post("/compliance/handoff", { task_id: task.id });
      setHandoff(data);
      setState("done");
      const leadTitle = data?.lead_specialist?.title || "Licensing Specialist";
      toast.success(
        `On it. My ${leadTitle} has the packet — my Filing Specialist is queuing it now.`,
        {
          duration: 5500,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                await api.post("/compliance/handoff/undo", { task_id: task.id });
                setState("idle");
                setHandoff(null);
                toast.message("Handoff canceled. I'm not filing anything.");
                onDone && onDone();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't undo — it may have already been filed.");
              }
            },
          },
        }
      );
      // Follow-up: show calendar coverage so users see the sync loop close in real time.
      try {
        const { data: cov } = await api.get("/calendar/coverage");
        if (cov?.synced > 0) {
          toast.message(
            `Calendar coverage: ${cov.synced} of ${cov.total} ${cov.percent === 100 ? "✓" : `· ${cov.percent}%`}`,
            { duration: 3500 }
          );
        }
      } catch { /* non-blocking */ }
      onDone && onDone();
    } catch (e) {
      setState("error");
      toast.error(e?.response?.data?.detail || "Dox couldn't queue this — try again.");
    }
  };

  return (
    <div
      className={`glass-card p-5 ${cls.split(" ")[0]} flex flex-col gap-4`}
      data-testid={`renewal-${task.id}`}
    >
      {/* Top line: title + urgency badge */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            {task.overdue && <AlertTriangle size={14} className="text-rose-400 shrink-0" />}
            <div className="font-semibold text-white leading-snug">{label}</div>
          </div>
          <div className="text-xs text-white/50">{rec.agency || task.jurisdiction || "Compliance filing"}</div>
        </div>
        <div className={`text-right shrink-0 ${cls.split(" ")[1] || ""}`}>
          <div className="font-display font-black text-2xl leading-none">
            {task.overdue ? `+${Math.abs(task.days_remaining)}` : task.days_remaining}
            <span className="text-xs align-top ml-0.5">d</span>
          </div>
          <div className="text-[10px] tracking-widest uppercase mt-0.5">
            {task.overdue ? "overdue" : `T-${task.reminder_tier || task.days_remaining}`}
          </div>
        </div>
      </div>

      {/* Dox voice — conversational prompt */}
      <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-3">
        {state === "idle" && (
          <>
            <div className="text-sm text-white/90">
              Your <span className="font-semibold">{label}</span> {whenPhrase}. Want me to renew it for you?
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <button
                data-testid={`yes-btn-${task.id}`}
                onClick={doYes}
                className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-black px-6 py-2.5 min-h-[44px] text-sm shadow-lg shadow-emerald-500/25 transition-all hover:shadow-emerald-400/40 hover:scale-[1.02] active:scale-95 flex items-center gap-2"
              >
                <Sparkles size={14} />
                YES — Renew it for me
              </button>
              {rec.typical_renewal_fee_usd ? (
                <span className="text-[11px] text-white/50">Est. fee ${rec.typical_renewal_fee_usd}</span>
              ) : null}
            </div>
          </>
        )}
        {state === "working" && (
          <div className="flex items-start gap-3">
            <Loader2 size={16} className="animate-spin text-emerald-300 mt-0.5 shrink-0" />
            <div className="text-sm text-white/85">
              <div>Gathering your documents…</div>
              <div className="opacity-70 text-xs mt-1">Filling forms · preparing the packet · handing off to the filing queue.</div>
            </div>
          </div>
        )}
        {state === "done" && (
          <div className="flex items-start gap-3">
            <CheckCircle2 size={16} className="text-emerald-400 mt-0.5 shrink-0" />
            <div className="text-sm text-white/90">
              <div className="font-semibold text-emerald-300">I&apos;m on it.</div>
              <div className="text-white/70 text-xs mt-1">
                I&apos;ve prepared your renewal package and queued it for filing. I&apos;ll monitor the agency and update
                you the moment it&apos;s confirmed.
              </div>
              {handoff?.submission_id && (
                <a
                  href="#submissions"
                  onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("dox:nav", { detail: "submissions" })); }}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-300 hover:text-emerald-200"
                >
                  Track it in Filings <ExternalLink size={11} />
                </a>
              )}
            </div>
          </div>
        )}
        {state === "error" && (
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-rose-400 mt-0.5 shrink-0" />
            <div className="text-sm text-white/85">
              <div>I couldn&apos;t queue this one just now. Give me another try?</div>
              <button
                onClick={doYes}
                className="mt-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-4 py-1.5 text-xs"
              >
                Try again
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Meta row (small, secondary) */}
      <div className="flex flex-wrap gap-1.5 items-center">
        <span className="text-[10px] rounded-full glass border border-white/10 px-2 py-0.5 text-white/50">Due {task.due_date}</span>
        {rec.official_link && (
          <a
            href={rec.official_link}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] rounded-full glass border border-white/10 px-2 py-0.5 text-white/60 hover:text-white hover:border-white/30 inline-flex items-center gap-1"
          >
            View agency <ExternalLink size={9} />
          </a>
        )}
      </div>
    </div>
  );
}

/** Compact Health Score badge for the dashboard header. */
export function HealthScoreBadge() {
  const [score, setScore] = useState(null);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/compliance/health-score");
        if (active) setScore(data);
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, []);
  if (!score) return null;
  const tone = score.score >= 90 ? "text-emerald-400" : score.score >= 70 ? "text-amber-300" : "text-rose-300";
  return (
    <div
      className="hidden md:inline-flex items-center gap-2 rounded-full glass-strong border border-white/10 px-3 py-1.5"
      data-testid="health-score-badge"
      title={score.top_fixes?.map((f) => f.reason).join(" · ") || "Compliance Health"}
    >
      <Radar size={12} className="text-dox-orange" />
      <span className="text-xs text-white/70 font-medium">Health</span>
      <span className={`text-xs font-bold ${tone}`}>{score.score}/100 · {score.grade}</span>
    </div>
  );
}
